// src/merchant/MerchantLayout.js
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, ShoppingBag,
  LogOut, Menu, X, ChevronRight,
  Store, User, IndianRupee,
} from 'lucide-react';
import { useApp } from '../context/AppContext';

const NAV = [
  { to: '/merchant',           icon: LayoutDashboard, label: 'Dashboard', exact: true  },
  { to: '/merchant/products',  icon: Package,          label: 'Products',  exact: false },
  { to: '/merchant/orders',    icon: ShoppingBag,      label: 'Orders',    exact: false },
  { to: '/merchant/earnings',  icon: IndianRupee,      label: 'Earnings',  exact: false },
  { to: '/merchant/profile',   icon: User,             label: 'Profile',   exact: false },
];

export default function MerchantLayout({ children }) {
  const { user, logout } = useApp();
  const location          = useLocation();
  const navigate          = useNavigate();
  const [open, setOpen]   = useState(false);

  const isActive = ({ to, exact }) =>
    exact ? location.pathname === to : location.pathname.startsWith(to);

  const currentNav = NAV.find((n) => isActive(n));

  const handleLogout = async () => { await logout(); navigate('/'); };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-white/10 flex-shrink-0">
        <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">♡</span>
          </div>
          <div>
            <p className="font-display font-bold text-white text-base leading-none">
              Couple<span className="text-rose-400">Cart</span>
            </p>
            <p className="text-gray-500 text-[10px] font-body mt-0.5 tracking-widest uppercase">Merchant Portal</p>
          </div>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map(({ to, icon: Icon, label, exact }) => {
          const active = isActive({ to, exact });
          return (
            <Link key={to} to={to} onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-body font-medium transition-all ${
                active
                  ? 'bg-rose-500 text-white shadow-[0_4px_15px_rgba(244,63,94,0.4)]'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}>
              <Icon size={18} /> {label}
              {active && <ChevronRight size={14} className="ml-auto" />}
            </Link>
          );
        })}
      </nav>

      {/* User + logout */}
      <div className="px-3 py-4 border-t border-white/10 flex-shrink-0 space-y-1">
        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5">
          <div className="w-8 h-8 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {user?.displayName?.[0]?.toUpperCase() || 'M'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-body text-sm font-medium truncate">{user?.displayName}</p>
            <p className="text-gray-500 font-body text-xs capitalize">{user?.role || 'merchant'}</p>
          </div>
        </div>
        <button onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-gray-400 hover:bg-white/5 hover:text-red-400 text-sm font-body transition-all">
          <LogOut size={16} /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">

      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-60 bg-[#0d0d0d] fixed top-0 left-0 bottom-0 z-40">
        <SidebarInner />
      </aside>

      {/* Mobile backdrop */}
      {open && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Mobile sidebar drawer */}
      <aside className={`fixed top-0 left-0 bottom-0 w-64 bg-[#0d0d0d] z-50 flex flex-col md:hidden transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex justify-end p-4 flex-shrink-0">
          <button onClick={() => setOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <SidebarInner />
      </aside>

      {/* Main content */}
      <div className="flex-1 md:ml-60 flex flex-col min-h-screen">

        {/* Topbar — no overlap because main Navbar is hidden on /merchant routes */}
        <header className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              {/* Hamburger — mobile only */}
              <button onClick={() => setOpen(true)}
                className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
                <Menu size={22} className="text-gray-600" />
              </button>
              <div>
                <h1 className="font-display font-bold text-gray-900 text-base leading-none">
                  {currentNav?.label || 'Dashboard'}
                </h1>
                <p className="text-rose-400 font-body text-xs font-bold uppercase tracking-wider mt-0.5">Merchant Portal</p>
              </div>
            </div>
            <Link to="/" target="_blank"
              className="flex items-center gap-1.5 text-xs font-body text-gray-500 hover:text-rose-500 border border-gray-200 hover:border-rose-300 px-3 py-2 rounded-xl transition-all">
              <Store size={14} /> View Store
            </Link>
          </div>
        </header>

        {/* Page content — extra bottom padding on mobile for bottom nav */}
        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8">
          {children}
        </main>
      </div>

      {/* ── Mobile bottom navigation bar ── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0d0d0d] border-t border-white/10 flex items-center">
        {NAV.map(({ to, icon: Icon, label, exact }) => {
          const active = isActive({ to, exact });
          return (
            <Link key={to} to={to}
              className={`flex-1 flex flex-col items-center justify-center py-2.5 gap-0.5 transition-all ${
                active ? 'text-rose-400' : 'text-gray-500'
              }`}>
              <Icon size={20} />
              <span className="text-[10px] font-body font-medium">{label}</span>
              {active && <span className="w-1 h-1 bg-rose-400 rounded-full" />}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
